/**
 * MACRO ARITHMETIC, SHARED BY THE WORKER AND THE SPA — which is why it lives in
 * src/shared/ beside types.ts rather than under worker/lib/.
 *
 * It was written under worker/lib/ and imported UP into the SPA. The reasoning for sharing
 * it was right: a second copy of the rounding and the partial-carrying rule in the frontend
 * is exactly how a previewed total and a stored total drift apart, and this module's whole
 * subject is totals you can trust. The DIRECTION was wrong. CONTRACT already names
 * src/shared as the boundary both sides import from, so living here needs no new precedent
 * and creates no dependency from the SPA bundle into Worker code.
 *
 * KEEP THIS FILE IMPORT-FREE. It is pure arithmetic on purpose, so either side can take it
 * without dragging a runtime along.
 */
/**
 * Macro arithmetic for the wellness module — the one place a total is ever computed.
 *
 * THE RULE THIS FILE EXISTS TO PROTECT: a missing macro is NULL, never 0, and a total built
 * out of parts where some part was missing must SAY SO. Adding numbers is the easy half;
 * carrying the ignorance forward is the half that gets dropped, and dropping it is what
 * turns "three ingredients, one with no sodium recorded" into a confident-looking sodium
 * figure that is short by a whole ingredient. That figure feeds a three-week
 * salt-sensitivity comparison, so a silent zero there is not a rounding error, it is a
 * wrong conclusion about a blood pressure medication.
 *
 * There are now THREE levels where this can go wrong, which is why the logic is shared:
 *   ingredient -> recipe   (rollUpIngredients)
 *   recipe     -> day      (rollUpMacros, over planned or logged meals)
 *   meal       -> day      (rollUpMacros)
 *
 * A `partial` marker therefore has to survive a level change, not just be recomputed at
 * each one. A recipe that is short on sodium produces a day that is short on sodium even
 * when it is the ONLY thing eaten that day — which a naive "some items were missing it"
 * test cannot see, because the one item it looked at had a number.
 *
 * THE RECIPE EDITOR IMPORTS THIS TOO, so that the live "macros so far" it shows while you
 * add ingredients is the same arithmetic that will be stored a second later, rather than a
 * lookalike that rounds differently or forgets to carry `partial`. A second copy of this
 * rule in the SPA is exactly how the rule would come apart. Keep this file free of Worker
 * globals and of every import — it has to bundle for the browser.
 */

export const MACRO_KEYS = [
  'calories',
  'protein_g',
  'fat_g',
  'carb_g',
  'sodium_mg',
  'potassium_mg',
] as const;

export type MacroKey = (typeof MACRO_KEYS)[number];

/** The six macros, each of which may be unknown. */
export type MacroSet = { [K in MacroKey]: number | null };

/**
 * Something that can be added up, which may already know its own total is incomplete.
 * `macros_partial` is the inherited ignorance — a recipe rolled up from ingredients where
 * one had no potassium arrives here already knowing potassium is understated.
 */
export interface MacroCarrier extends MacroSet {
  macros_partial?: string[] | null;
}

export interface MacroRollup extends MacroSet {
  /** Macro keys whose total is present but SHORT. Ordered as MACRO_KEYS. */
  partial: MacroKey[];
}

export const emptyMacros = (): MacroSet => ({
  calories: null,
  protein_g: null,
  fat_g: null,
  carb_g: null,
  sodium_mg: null,
  potassium_mg: null,
});

/** Parse the stored JSON array. Anything unreadable is treated as "nothing known missing". */
export function parsePartial(json: string | null | undefined): MacroKey[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    if (!Array.isArray(v)) return [];
    return MACRO_KEYS.filter((k) => v.includes(k));
  } catch {
    return [];
  }
}

/** Store as JSON, or NULL when nothing is missing — an empty array is noise in the column. */
export function serializePartial(keys: readonly MacroKey[]): string | null {
  return keys.length ? JSON.stringify(keys) : null;
}

/**
 * Add up a list of items, naming every macro whose total is incomplete.
 *
 * A macro is:
 *   null            — NOTHING in the list carried it. There is no total to give.
 *   a number        — at least one item carried it; the sum of those that did.
 *   in `partial`    — some item was missing it, or some item said its own value was short.
 *
 * The two ways to land in `partial` are both real and neither implies the other. An item
 * with a null value is visibly missing. An item with a NUMBER and a `macros_partial` entry
 * has a value that is itself understated — invisible from the outside, which is exactly why
 * it has to be carried rather than re-derived.
 */
export function rollUpMacros(items: MacroCarrier[]): MacroRollup {
  const out = { ...emptyMacros(), partial: [] as MacroKey[] };

  for (const key of MACRO_KEYS) {
    const present = items.filter((m) => m[key] !== null && m[key] !== undefined);
    if (!present.length) continue;

    out[key] = present.reduce((sum, m) => sum + (m[key] as number), 0);

    const someBlank = present.length < items.length;
    const someShort = items.some((m) => m.macros_partial?.includes(key));
    if (someBlank || someShort) out.partial.push(key);
  }

  return out;
}

/** One line of a recipe: how many of an ingredient's stated portions it uses. */
export interface MacroLine {
  quantity: number;
  macros: MacroSet;
}

/**
 * Roll a recipe's ingredient lines up into PER-SERVING macros.
 *
 * Ingredient lines describe the WHOLE recipe; the macro columns on a recipe are per
 * serving, because that is the unit a slot in the week is planned in. So the sum is divided
 * by `servings` at the end rather than each line being pre-divided — dividing first and
 * summing after would round six times instead of once.
 *
 * Rounded to whole numbers because that is what the columns hold and what `macro()` renders.
 * The rounding happens once, here, on the final per-serving figure; nothing accumulates it,
 * because a re-edit recomputes from the ingredient values again rather than from this.
 */
export function rollUpIngredients(lines: MacroLine[], servings: number): MacroRollup {
  const per = Math.max(1, Math.round(servings) || 1);

  const scaled: MacroCarrier[] = lines.map((line) => {
    const row = emptyMacros() as MacroCarrier;
    for (const key of MACRO_KEYS) {
      const v = line.macros[key];
      row[key] = v === null ? null : v * line.quantity;
    }
    return row;
  });

  const rolled = rollUpMacros(scaled);
  for (const key of MACRO_KEYS) {
    if (rolled[key] !== null) rolled[key] = Math.round((rolled[key] as number) / per);
  }
  return rolled;
}
